import { eventColor } from './eventColors';
import type { TaskPriority, Project } from './types';
import { categoryIcons } from './utils';

/** Spanning-bar style colors: light bg + dark text, matching the calendar views. */
const SPAN_COLORS: Record<string, { bg: string; fg: string }> = {
  appointment: { bg: '#dbeafe', fg: '#1e40af' },
  travel: { bg: '#ede9fe', fg: '#5b21b6' },
  event: { bg: '#dcfce7', fg: '#166534' },
  deadline: { bg: '#fee2e2', fg: '#991b1b' },
};
const SPAN_DEFAULT = { bg: '#f3f4f6', fg: '#374151' };

function contrastTextColor(background: string): string {
  const hex = background.replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(hex)) return '#172033';

  const channels = [0, 2, 4].map((start) => Number.parseInt(hex.slice(start, start + 2), 16) / 255);
  const luminance = channels
    .map((channel) => (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4))
    .reduce((total, channel, index) => total + channel * [0.2126, 0.7152, 0.0722][index], 0);

  return (luminance + 0.05) / 0.05 > 1.05 / (luminance + 0.05) ? '#111827' : '#ffffff';
}

/** Creates a category-colored chip drag preview for habits and categories. */
export function setCategoryDragImage(e: DragEvent, label: string, color: string): void {
  if (!e.dataTransfer) return;

  const el = document.createElement('div');
  el.textContent = label;
  Object.assign(el.style, {
    position: 'fixed',
    top: '-1000px',
    left: '0px',
    maxWidth: '220px',
    padding: '5px 9px',
    overflow: 'hidden',
    border: '1px solid rgba(15, 23, 42, 0.18)',
    borderRadius: '999px',
    background: color,
    color: contrastTextColor(color),
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.2)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '12px',
    fontWeight: '700',
    lineHeight: '1.2',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    pointerEvents: 'none',
    zIndex: '-1',
  });

  document.body.appendChild(el);
  e.dataTransfer.setDragImage(el, 12, 12);
  requestAnimationFrame(() => el.remove());
}

/**
 * Creates a compact, fixed-size drag preview for calendar events.
 *
 * - Single-day events: white background with a colored left accent border.
 * - Multi-day events: solid colored background matching the spanning bar style.
 */
export function setCompactDragImage(
  e: DragEvent,
  title: string,
  type: string,
  multiDay = false,
): void {
  if (!e.dataTransfer) return;

  const el = document.createElement('div');
  el.textContent = title;

  const shared: Record<string, string> = {
    position: 'fixed',
    top: '-1000px',
    left: '0px',
    minWidth: '80px',
    maxWidth: '140px',
    padding: '3px 6px',
    fontSize: '10px',
    fontWeight: '600',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    lineHeight: '1.3',
    borderRadius: '3px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    pointerEvents: 'none',
    zIndex: '-1',
  };

  if (multiDay) {
    const colors = SPAN_COLORS[type] || SPAN_DEFAULT;
    Object.assign(shared, {
      background: colors.bg,
      color: colors.fg,
      border: 'none',
      boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
    });
  } else {
    const color = eventColor(type);
    Object.assign(shared, {
      background: 'white',
      color: '#1f2937',
      border: 'none',
      borderLeft: `2px solid ${color}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
    });
  }

  Object.assign(el.style, shared);
  document.body.appendChild(el);

  e.dataTransfer.setDragImage(el, 10, 10);

  requestAnimationFrame(() => {
    document.body.removeChild(el);
  });
}

const PRIORITY_ICONS: Record<TaskPriority, string> = {
  none: '',
  low: '🔵',
  medium: '🟠',
  high: '🔴',
};

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  none: '#9ca3af',
  low: '#3b82f6',
  medium: '#fb923c',
  high: '#ef4444',
};

/**
 * Creates a compact, fixed-size drag preview for task items.
 *
 * Shows a checkbox indicator, task title (truncated), and optional priority icon.
 * Styled consistently with the event drag preview.
 */
export function setCompactTaskDragImage(
  e: DragEvent,
  title: string,
  priority: TaskPriority = 'none',
): void {
  if (!e.dataTransfer) return;

  const el = document.createElement('div');

  // Build content: checkbox dot + title + optional priority
  const checkbox = document.createElement('span');
  checkbox.textContent = '○';
  Object.assign(checkbox.style, {
    marginRight: '5px',
    fontSize: '10px',
    color: '#9ca3af',
    flexShrink: '0',
  });

  const titleSpan = document.createElement('span');
  titleSpan.textContent = title;
  Object.assign(titleSpan.style, {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '1',
    minWidth: '0',
  });

  el.appendChild(checkbox);
  el.appendChild(titleSpan);

  if (priority !== 'none') {
    const badge = document.createElement('span');
    badge.textContent = PRIORITY_ICONS[priority];
    Object.assign(badge.style, {
      marginLeft: '4px',
      fontSize: '9px',
      flexShrink: '0',
    });
    el.appendChild(badge);
  }

  Object.assign(el.style, {
    position: 'fixed',
    top: '-1000px',
    left: '0px',
    display: 'flex',
    alignItems: 'center',
    minWidth: '100px',
    maxWidth: '200px',
    padding: '4px 8px',
    fontSize: '11px',
    fontWeight: '500',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    lineHeight: '1.4',
    borderRadius: '4px',
    background: 'white',
    color: '#1f2937',
    border: '1px solid #e5e7eb',
    borderLeft: `3px solid ${PRIORITY_COLORS[priority]}`,
    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
    pointerEvents: 'none',
    zIndex: '-1',
  });

  document.body.appendChild(el);
  e.dataTransfer.setDragImage(el, 10, 10);

  requestAnimationFrame(() => {
    document.body.removeChild(el);
  });
}

/** Status → left-border accent color for project drag previews. */
const PROJECT_STATUS_COLORS: Record<string, string> = {
  planned: '#3b82f6',
  active: '#d97706',
  almost: '#ea580c',
  done: '#16a34a',
};

/**
 * Creates a compact drag preview for project items.
 *
 * Shows category emoji, project title (truncated), and a status-colored left
 * accent border — matching the visual language of project chips.
 */
export function setCompactProjectDragImage(e: DragEvent, project: Project): void {
  if (!e.dataTransfer) return;

  const el = document.createElement('div');

  const icon = document.createElement('span');
  icon.textContent = categoryIcons[project.category] || '📋';
  Object.assign(icon.style, {
    marginRight: '4px',
    fontSize: '10px',
    flexShrink: '0',
  });

  const titleSpan = document.createElement('span');
  titleSpan.textContent = project.title;
  Object.assign(titleSpan.style, {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '1',
    minWidth: '0',
  });

  el.appendChild(icon);
  el.appendChild(titleSpan);

  if (project.isPriority) {
    const star = document.createElement('span');
    star.textContent = '★';
    Object.assign(star.style, {
      marginLeft: '4px',
      fontSize: '9px',
      color: '#facc15',
      flexShrink: '0',
    });
    el.appendChild(star);
  }

  const accentColor = PROJECT_STATUS_COLORS[project.status] || '#9ca3af';

  Object.assign(el.style, {
    position: 'fixed',
    top: '-1000px',
    left: '0px',
    display: 'flex',
    alignItems: 'center',
    minWidth: '80px',
    maxWidth: '160px',
    padding: '3px 6px',
    fontSize: '10px',
    fontWeight: '600',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    lineHeight: '1.3',
    borderRadius: '3px',
    background: 'white',
    color: '#374151',
    border: 'none',
    borderLeft: `3px solid ${accentColor}`,
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    pointerEvents: 'none',
    zIndex: '-1',
  });

  document.body.appendChild(el);
  e.dataTransfer.setDragImage(el, 10, 10);

  requestAnimationFrame(() => {
    document.body.removeChild(el);
  });
}
